import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { toast } from 'react-toastify';

import { UpdatePHIForm, UpdatePHIFormValues } from 'src/components/forms/UpdatePHIForm';
import { PatientHealthInfo, patientService } from 'src/services/patient';
import { FundCodes } from 'src/utils/fundCodes';
import { useSelector } from 'src/store';
import moment from "moment";

type Props = {
    className?: string;
    patientHealthInfo?: PatientHealthInfo;
    reloadHealthInfo(): void;
    setIsDirty?(isDirty: boolean): void;
}

export function UpdatePHI({ className, patientHealthInfo, reloadHealthInfo, setIsDirty }: Props) {
    const hasExistingPHI = (patientHealthInfo?.health_insurance_name !== "");
    const [isUpdateForm, setIsUpdateForm] = useState(!hasExistingPHI); // default to update if no health_ins_name recorded
    const siteId = useSelector(state => state.appointment.clinicId);
    const isNewPatient = useSelector(state => state.appointment.isNewPatient);

    const defaultValues: Partial<UpdatePHIFormValues> = useMemo(() => {
        const fund = FundCodes.find(f => f.FundName === patientHealthInfo?.health_insurance_name);
        return {
            havePhi: hasExistingPHI ? "yes" : "no",
            customerId: patientHealthInfo?.health_insurance_no,
            providerId: patientHealthInfo?.health_insurance_name
                ? fund?.FundID ?? 9999
                : "",
            providerName: patientHealthInfo
                ? fund?.FundName ?? patientHealthInfo.health_insurance_name
                : "",
            expiry: patientHealthInfo?.health_insurance_expiry_date &&
                (patientHealthInfo.health_insurance_expiry_date !== "2099-12-12"
                    ? moment(patientHealthInfo.health_insurance_expiry_date).format("yyyy-MM")
                    : undefined)  // Adjust for month input.
        }
    }, [hasExistingPHI, patientHealthInfo]);

    const doUpdate = useCallback(async (values: UpdatePHIFormValues) => {
        try {
            await patientService.UpdatePatientHealthInfo(siteId, {
                Name: values.providerId !== 9999 && FundCodes.find(f => values.providerId === f.FundID)?.FundName || values.providerName,
                Number: values.customerId,
                ExpiryDate: values.expiry
            });

            toast.success("Your Private Health Insurance has been updated!");
            reloadHealthInfo();
            setIsUpdateForm(false);
            setIsDirty?.(false);
        } catch (e) {
            if (e instanceof Error) {
                toast.error(e.message || e.name);
            }
        }
    }, [reloadHealthInfo, setIsDirty, siteId]);

    return <div className={classNames(className)}>
        {!isUpdateForm
            ? <div className='flex flex-row gap-1'>
                {patientHealthInfo && <div className="flex-1">
                    <h3 className='font-semibold text-gray-700'>{patientHealthInfo.health_insurance_name}</h3>
                    <p>
                        {patientHealthInfo.health_insurance_no}
                        {patientHealthInfo.health_insurance_expiry_date &&
                            patientHealthInfo.health_insurance_expiry_date !== '2099-12-12'
                            ? `, ${moment(patientHealthInfo.health_insurance_expiry_date).format("MM-YYYY")}`
                            : ""}
                    </p>
                </div>}
                <button
                    onClick={() => setIsUpdateForm(true)}
                    className='mt-2 text-primary-700 font-semibold disabled:text-gray'
                >Edit</button>
            </div>
            : <UpdatePHIForm
                required={isNewPatient}
                canRemovePHI={!hasExistingPHI}
                canCancel={true}
                defaultValues={defaultValues}
                onCancel={() => setIsUpdateForm(!hasExistingPHI)}
                onSubmit={doUpdate}
                setIsDirty={setIsDirty}
            />
        }
    </div>
}